import {
  ISecureInformationRequestField,
  useSecureInformationRequest,
} from '@commenda/api';
import {
  Alert,
  Badge,
  Button,
  CountryCode,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  FlagComponent,
  Icons,
  Loading,
} from '@commenda/kakashi';
import {
  copyToClipboard,
  getPrintableAddress,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import {
  RecurringUserTaskStatus,
  SecureInformationRequestFieldType,
} from '@prisma/client';
import { DataPoint } from '../../Corporation/common/DataPoint';
import FileDownloader from '../../components/file-downloader-wrapper';
import { useNotification } from '../../hooks/useNotification';

const SecureInformationRequestAnswersSummaryModal = ({
  isOpen,
  onClose,
  secureInformationRequestId,
}: {
  isOpen: boolean;
  onClose: () => void;
  secureInformationRequestId: string;
}) => {
  const {
    data: secureInformationRequest,
    error: secureInformationRequestError,
  } = useSecureInformationRequest(secureInformationRequestId);

  const { setNotification } = useNotification();

  if (!secureInformationRequest) {
    if (secureInformationRequestError)
      console.error(secureInformationRequestError);

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-[1000px]">
          <DialogHeader className="flex flex-col gap-xs items-start">
            <DialogTitle>Secure Information Request</DialogTitle>
          </DialogHeader>
          <div className="h-[440px] flex justify-center items-center">
            <Loading />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isPrimitiveField = (field: ISecureInformationRequestField) => {
    return (
      field.type === SecureInformationRequestFieldType.TEXT ||
      field.type === SecureInformationRequestFieldType.NUMBER ||
      field.type === SecureInformationRequestFieldType.DATE ||
      field.type === SecureInformationRequestFieldType.RADIO ||
      field.type === SecureInformationRequestFieldType.BIG_TEXT ||
      field.type === SecureInformationRequestFieldType.SELECT ||
      field.type === SecureInformationRequestFieldType.PHONE_NUMBER
    );
  };

  const formatFieldValue = (field: ISecureInformationRequestField) => {
    if (field.type === SecureInformationRequestFieldType.RADIO)
      return field.decryptedValue === 'false' ? 'No' : 'Yes';

    if (field.type === SecureInformationRequestFieldType.DATE)
      return field.decryptedValue
        ? NonTimezoneSensitiveDate.fromISO8601String(
            field.decryptedValue
          ).format('MMM DD, YYYY')
        : 'N/A';

    if (field.type === SecureInformationRequestFieldType.MULTI_SELECT)
      return field.selectResponse?.join(', ');

    if (field.type === SecureInformationRequestFieldType.ADDRESS)
      return getPrintableAddress(field.address);

    return field.decryptedValue;
  };

  const isFileUploadField = (field: ISecureInformationRequestField) => {
    return (
      field.type === SecureInformationRequestFieldType.FILE ||
      field.type === SecureInformationRequestFieldType.MULTI_FILE
    );
  };

  const onCopy = (field: ISecureInformationRequestField) => {
    const value = formatFieldValue(field);

    copyToClipboard(value);

    setNotification({
      valid: true,
      title: 'Copied to clipboard',
      show: true,
      message: 'Successfully copied to clipboard',
    });
  };

  const isCompleted =
    secureInformationRequest.recurringUserTask?.status ===
    RecurringUserTaskStatus.COMPLETED;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[1000px]">
        <DialogHeader className="flex flex-col gap-xs items-start">
          <DialogTitle>
            <div className="flex flex-col gap-xs items-start">
              <div className="flex items-center gap-md">
                <h3 className="txt-large-bold">
                  {secureInformationRequest.title}
                </h3>
                <div className="flex items-center gap-lg">
                  <div className="size-1 rounded-full bg-fg-tertiary" />
                  <div className="flex gap-md items-center">
                    <FlagComponent
                      country={
                        secureInformationRequest.corporation
                          ?.incorporationCountry as CountryCode
                      }
                      countryName={
                        secureInformationRequest.corporation
                          ?.incorporationCountry
                      }
                      _className="size-4"
                    />
                    <span className="txt-medium-regular text-text-tertiary">
                      {secureInformationRequest.corporation?.displayName}
                    </span>
                  </div>
                </div>
              </div>
              <p className="txt-small-regular text-text-tertiary">
                Information request{' '}
                {secureInformationRequest.recurringUserTask?.status ===
                RecurringUserTaskStatus.COMPLETED
                  ? 'completed on:'
                  : 'sent on:'}{' '}
                {secureInformationRequest.lastModifiedAt
                  ? NonTimezoneSensitiveDate.fromNativeDate(
                      new Date(secureInformationRequest.lastModifiedAt)
                    ).format('MMM DD, YYYY')
                  : 'N/A'}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="p-2xl flex flex-col gap-2xl max-h-[600px] overflow-y-auto">
          {secureInformationRequest.recurringUserTask?.status !==
          RecurringUserTaskStatus.COMPLETED ? (
            <Alert
              variant="warning"
              icon={<Icons.Hourglass01 className="size-5 text-fg-warning" />}
              description="The client has not submitted a response yet."
            />
          ) : null}
          <div className="flex flex-col gap-3xl">
            {secureInformationRequest.groups
              .sort((a, b) => a.indexInRequest - b.indexInRequest)
              .map((group) => (
                <div key={group.id} className="flex flex-col gap-xl">
                  <h3 className="txt-medium-bold">{group.headerText}</h3>
                  <div className="flex flex-col rounded-xl border border-border-primary bg-bg-primary overflow-clip divide-y divide-border-primary">
                    {group.fields
                      .sort((a, b) => a.indexInGroup - b.indexInGroup)
                      .map((field) => (
                        <DataPoint
                          key={field.id}
                          title={field.headerText}
                          value={isCompleted ? formatFieldValue(field) : ''}
                          {...(isPrimitiveField(field) &&
                          (!field.decryptedValue || !isCompleted)
                            ? {
                                reactNode: (
                                  <Badge size="sm" variant="warning">
                                    Info missing
                                  </Badge>
                                ),
                              }
                            : {})}
                          {...((field.type ===
                            SecureInformationRequestFieldType.ADDRESS &&
                            !field.address) ||
                          (field.type ===
                            SecureInformationRequestFieldType.MULTI_SELECT &&
                            !field.selectResponse?.length)
                            ? {
                                reactNode: (
                                  <Badge size="sm" variant="warning">
                                    Info missing
                                  </Badge>
                                ),
                              }
                            : {})}
                          hideAddAction
                          {...(isFileUploadField(field)
                            ? field.files.length && isCompleted
                              ? {
                                  reactNode: (
                                    <div className="flex flex-wrap items-center gap-sm">
                                      {field.files.map((fl) => (
                                        <FileDownloader
                                          key={fl.id}
                                          file={{
                                            id: fl.id,
                                            fileName: fl.fileName,
                                          }}
                                          token={undefined}
                                        >
                                          <Button variant="link">
                                            {fl.fileName}
                                          </Button>
                                        </FileDownloader>
                                      ))}
                                    </div>
                                  ),
                                }
                              : {
                                  reactNode: (
                                    <Badge size="sm" variant="warning">
                                      Info missing
                                    </Badge>
                                  ),
                                }
                            : {})}
                          {...(!isFileUploadField(field)
                            ? {
                                onCopy: () => onCopy(field),
                              }
                            : {})}
                        />
                      ))}
                  </div>
                </div>
              ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { SecureInformationRequestAnswersSummaryModal };
